import type { SkillId } from './catalog.ts';
import type { GameState } from './types.ts';

export type ArtifactCategory = 'combat' | 'gathering' | 'artisan' | 'adventure' | 'realm';
export type ArtifactRarity = 'common' | 'rare' | 'mythic';

export interface ArtifactDefinition {
  id: string;
  icon: string;
  name: string;
  description: string;
  skill?: SkillId;
  effect?: 'manual' | 'offline' | 'sale' | 'xp' | 'hallCost' | 'toolCost';
  category?: ArtifactCategory;
  rarity?: ArtifactRarity;
  xpSkills?: readonly SkillId[];
  xpBonus?: number;
}

export const ARTIFACT_CATEGORY_LABELS: Record<ArtifactCategory, string> = {
  combat: 'Combat', gathering: 'Gathering', artisan: 'Artisan', adventure: 'Adventure', realm: 'Realm'
};

export const ARTIFACT_RARITY_WEIGHTS: Record<ArtifactRarity, number> = { common: 1, rare: 0.22, mythic: 0.035 };
export const ARTIFACT_DUST_COSTS: Record<ArtifactRarity, number> = { common: 80, rare: 300, mythic: 1_200 };
export const ARTIFACT_DUPLICATE_DUST: Record<ArtifactRarity, number> = { common: 8, rare: 35, mythic: 150 };

export const SKILLS_BY_ARTIFACT_CATEGORY: Record<Exclude<ArtifactCategory, 'realm'>, readonly SkillId[]> = {
  combat: ['Attack', 'Strength', 'Defence', 'Constitution', 'Ranged', 'Magic', 'Prayer', 'Slayer'],
  gathering: ['Woodcutting', 'Mining', 'Fishing', 'Farming', 'Hunter', 'Archaeology', 'Divination'],
  artisan: ['Smithing', 'Cooking', 'Firemaking', 'Crafting', 'Fletching', 'Herblore', 'Construction', 'Runecrafting'],
  adventure: ['Agility', 'Thieving', 'Dungeoneering', 'Invention', 'Summoning']
};

const CATEGORY_BY_SKILL = Object.fromEntries(
  Object.entries(SKILLS_BY_ARTIFACT_CATEGORY).flatMap(([category, skills]) => skills.map((skill) => [skill, category]))
) as Record<SkillId, Exclude<ArtifactCategory, 'realm'>>;

const SKILL_ARTIFACTS: readonly ArtifactDefinition[] = [
  { id: 'bloodforged_hilt', icon: '⚔️', name: 'Bloodforged Hilt', description: '+1% Weaponry production', skill: 'Attack' },
  { id: 'titans_knuckle', icon: '💪', name: "Titan's Knuckle", description: '+1% Might production', skill: 'Strength' },
  { id: 'aegis_fragment', icon: '🛡️', name: 'Aegis Fragment', description: '+1% Armour production', skill: 'Defence' },
  { id: 'hawk_eye_fletching', icon: '🏹', name: 'Hawk-Eye Fletching', description: '+1% Arrowheads production', skill: 'Ranged' },
  { id: 'whispered_bead', icon: '🙏', name: 'Whispered Bead', description: '+1% Faith production', skill: 'Prayer' },
  { id: 'starless_focus', icon: '✨', name: 'Starless Focus', description: '+1% Arcana production', skill: 'Magic' },
  { id: 'recursive_rune', icon: '🔮', name: 'Recursive Rune', description: '+1% Runes production', skill: 'Runecrafting' },
  { id: 'impossible_plumbline', icon: '🏗️', name: 'Impossible Plumbline', description: '+1% Planks production', skill: 'Construction' },
  { id: 'everbeating_vial', icon: '❤️', name: 'Everbeating Vial', description: '+1% Vitality production', skill: 'Constitution' },
  { id: 'windstep_lace', icon: '🏃', name: 'Windstep Lace', description: '+1% Reflexes production', skill: 'Agility' },
  { id: 'glassroot_mortar', icon: '🧪', name: 'Glassroot Mortar', description: '+1% Herbs production', skill: 'Herblore' },
  { id: 'key_without_teeth', icon: '🗝️', name: 'Key Without Teeth', description: '+1% Secrets production', skill: 'Thieving' },
  { id: 'moonspun_needle', icon: '🧵', name: 'Moonspun Needle', description: '+1% Cloth production', skill: 'Crafting' },
  { id: 'feather_of_returning', icon: '🪶', name: 'Feather of Returning', description: '+1% Feathers production', skill: 'Fletching' },
  { id: 'unclaimed_scale', icon: '🐲', name: 'Unclaimed Scale', description: '+1% Trophies production', skill: 'Slayer' },
  { id: 'seed_of_four_seasons', icon: '🌾', name: 'Seed of Four Seasons', description: '+1% Crops production', skill: 'Farming' },
  { id: 'trackless_antler', icon: '🦌', name: 'Trackless Antler', description: '+1% Hides production', skill: 'Hunter' },
  { id: 'ore_that_hums', icon: '⛏️', name: 'Ore That Hums', description: '+1% Ore production', skill: 'Mining' },
  { id: 'cold_forge_spark', icon: '🔨', name: 'Cold-Forge Spark', description: '+1% Bars production', skill: 'Smithing' },
  { id: 'tidebound_hook', icon: '🎣', name: 'Tidebound Hook', description: '+1% Fish production', skill: 'Fishing' },
  { id: 'bottomless_ladle', icon: '🍲', name: 'Bottomless Ladle', description: '+1% Food production', skill: 'Cooking' },
  { id: 'ash_that_refuses', icon: '🔥', name: 'Ash That Refuses', description: '+1% Charcoal production', skill: 'Firemaking' },
  { id: 'verdant_memory', icon: '🌿', name: 'Verdant Memory', description: '+1% Wood production', skill: 'Woodcutting' },
  { id: 'echo_in_amber', icon: '🌌', name: 'Echo in Amber', description: '+1% Memories production', skill: 'Divination' },
  { id: 'doorway_shard', icon: '🏰', name: 'Doorway Shard', description: '+1% Shards production', skill: 'Dungeoneering' },
  { id: 'self_turning_cog', icon: '⚙️', name: 'Self-Turning Cog', description: '+1% Components production', skill: 'Invention' },
  { id: 'yesterdays_trowel', icon: '🏺', name: "Yesterday's Trowel", description: '+1% Artifacts production', skill: 'Archaeology' },
  { id: 'unhatched_echo', icon: '🦅', name: 'Unhatched Echo', description: '+1% Charms production', skill: 'Summoning' }
];

const REALM_ARTIFACTS: readonly ArtifactDefinition[] = [
  { id: 'ember_of_motion', icon: '🔥', name: 'Ember of Motion', description: '+1% manual training power', effect: 'manual' },
  { id: 'dreamers_hourglass', icon: '⌛', name: "Dreamer's Hourglass", description: '+1% offline efficiency', effect: 'offline' },
  { id: 'coin_of_echoes', icon: '🪙', name: 'Coin of Echoes', description: '+1% resource sale value', effect: 'sale' },
  { id: 'shardglass_compass', icon: '🧭', name: 'Shardglass Compass', description: '+1% global skill XP', effect: 'xp' },
  { id: 'weavers_knot', icon: '🪢', name: "Weaver's Knot", description: '-1% Hall material costs', effect: 'hallCost' },
  { id: 'silent_crown', icon: '👑', name: 'The Silent Crown', description: '-1% Tool gold costs', effect: 'toolCost' }
];

const GROUP_ARTIFACTS: readonly ArtifactDefinition[] = [
  { id: 'banner_of_eight_trials', icon: '🚩', name: 'Banner of Eight Trials', description: '+3% XP for every Combat skill', category: 'combat', rarity: 'rare', xpSkills: SKILLS_BY_ARTIFACT_CATEGORY.combat, xpBonus: 0.03 },
  { id: 'wildsong_reliquary', icon: '🪺', name: 'Wildsong Reliquary', description: '+3% XP for every Gathering skill', category: 'gathering', rarity: 'rare', xpSkills: SKILLS_BY_ARTIFACT_CATEGORY.gathering, xpBonus: 0.03 },
  { id: 'impossible_blueprint', icon: '📐', name: 'The Impossible Blueprint', description: '+3% XP for every Artisan skill', category: 'artisan', rarity: 'rare', xpSkills: SKILLS_BY_ARTIFACT_CATEGORY.artisan, xpBonus: 0.03 },
  { id: 'wayfarers_lantern', icon: '🏮', name: "Wayfarer's Lantern", description: '+3% XP for every Adventure skill', category: 'adventure', rarity: 'rare', xpSkills: SKILLS_BY_ARTIFACT_CATEGORY.adventure, xpBonus: 0.03 }
];

const MYTHIC_ARTIFACTS: readonly ArtifactDefinition[] = [
  { id: 'hour_of_the_war_god', icon: '⏱️', name: "War God's Broken Hour", description: '+7% Attack, Strength and Defence XP', category: 'combat', rarity: 'mythic', xpSkills: ['Attack', 'Strength', 'Defence'], xpBonus: 0.07 },
  { id: 'heart_of_the_worldroot', icon: '🌳', name: 'Heart of the Worldroot', description: '+7% Woodcutting, Farming and Herblore XP', category: 'gathering', rarity: 'mythic', xpSkills: ['Woodcutting', 'Farming', 'Herblore'], xpBonus: 0.07 },
  { id: 'first_flame_anvil', icon: '🌋', name: 'Anvil of the First Flame', description: '+7% Mining, Smithing and Firemaking XP', category: 'artisan', rarity: 'mythic', xpSkills: ['Mining', 'Smithing', 'Firemaking'], xpBonus: 0.07 },
  { id: 'atlas_beyond_doors', icon: '🗺️', name: 'Atlas Beyond All Doors', description: '+7% Agility, Thieving and Dungeoneering XP', category: 'adventure', rarity: 'mythic', xpSkills: ['Agility', 'Thieving', 'Dungeoneering'], xpBonus: 0.07 }
];

export const ARTIFACTS: readonly ArtifactDefinition[] = [...SKILL_ARTIFACTS, ...REALM_ARTIFACTS, ...GROUP_ARTIFACTS, ...MYTHIC_ARTIFACTS];

export function artifactCategory(artifact: ArtifactDefinition): ArtifactCategory {
  if (artifact.category) return artifact.category;
  return artifact.skill ? CATEGORY_BY_SKILL[artifact.skill] : 'realm';
}

export function artifactRarity(artifact: ArtifactDefinition): ArtifactRarity {
  if (artifact.rarity) return artifact.rarity;
  return artifact.effect ? 'rare' : 'common';
}

export function artifactDustCost(artifact: ArtifactDefinition): number {
  return ARTIFACT_DUST_COSTS[artifactRarity(artifact)];
}

export function artifactDuplicateDust(artifact: ArtifactDefinition): number {
  return ARTIFACT_DUPLICATE_DUST[artifactRarity(artifact)];
}

function owns(state: GameState, id: string): boolean {
  return state.prestige.activities?.artifacts?.includes(id) ?? false;
}

export function artifactProductionMultiplier(state: GameState, skill: SkillId): number {
  const personal = ARTIFACTS.some((artifact) => artifact.skill === skill && owns(state, artifact.id)) ? 0.01 : 0;
  return 1 + personal + artifactSetTier(state, CATEGORY_BY_SKILL[skill]) * 0.01;
}

export function artifactSetTier(state: GameState, category: ArtifactCategory): number {
  if (category === 'realm') return 0;
  const found = ARTIFACTS.filter((artifact) => artifactCategory(artifact) === category && owns(state, artifact.id)).length;
  return found >= 5 ? 2 : found >= 3 ? 1 : 0;
}

export function artifactXpMultiplier(state: GameState, skill: SkillId = state.run.activeSkill): number {
  const targeted = ARTIFACTS.reduce((bonus, artifact) => bonus + (
    artifact.xpSkills?.includes(skill) && owns(state, artifact.id) ? artifact.xpBonus ?? 0 : 0
  ), 0);
  const global = owns(state, 'shardglass_compass') ? 0.01 : 0;
  return 1 + targeted + global + artifactSetTier(state, CATEGORY_BY_SKILL[skill]) * 0.01;
}

export function artifactManualMultiplier(state: GameState): number {
  return owns(state, 'ember_of_motion') ? 1.01 : 1;
}

export function artifactOfflineEfficiencyBonus(state: GameState): number {
  return owns(state, 'dreamers_hourglass') ? 0.01 : 0;
}

export function artifactSaleMultiplier(state: GameState): number {
  return owns(state, 'coin_of_echoes') ? 1.01 : 1;
}

export function artifactHallCostMultiplier(state: GameState): number {
  return owns(state, 'weavers_knot') ? 0.99 : 1;
}

export function artifactToolCostMultiplier(state: GameState): number {
  return owns(state, 'silent_crown') ? 0.99 : 1;
}
